import axios, { AxiosResponse, AxiosError } from 'axios';
import { createEventReuqestHeaders } from 'constants/request.constants';
import EventManagerError from './event-manager-error';

import { PLATFORM_TYPES } from '@sitegen/common/constants/platforms';

// utils
import { camelize } from 'utils/camelize';
import {
    buildEventManagerGetURL,
    buildEventManagerPostURL,
    normalizeEventPlatforms
} from 'modules/event-manager/event-manager.utils';

export interface IPlatformEvent {
    platform: PLATFORM_TYPES | string;
    readonly url: string | null;
    readonly startDate: string;
    readonly endDate: string;
    readonly preloadDate: string;
    readonly gameId?: string;
}

export interface IEventRequest {
    code: string;
    country: string;
}

export interface IOwnershipRequest {
    eventCode: string;
    userId: string;
    UbisoftConnectTicket: string;
    applicationId: string;
}

export interface IEventResponse {
    code: string;
    name: string;
    platforms: IPlatformEvent[];
}

export interface IEventOwnershipResponse extends IEventRequest {
    gameId: string;
}

interface IEventManagerService {
    requestEvents: (envName: string, data: IEventRequest) => Promise<Partial<IEventResponse> | EventManagerError>;
    applyOwnership: (envName: string, data: IOwnershipRequest) => Promise<Partial<IEventResponse> | EventManagerError>;
}

class EventManagerService implements IEventManagerService {
    public requestEvents(envName: string, data: IEventRequest): Promise<Partial<IEventResponse> | EventManagerError> {
        const endpointToCall = buildEventManagerGetURL(envName, data);

        return axios
            .get(endpointToCall)
            .then((response: AxiosResponse<IEventResponse>) => {
                const camelizedResponseData = camelize<IEventResponse>(response.data) as IEventResponse;

                return {
                    ...camelizedResponseData,
                    // Convert event platform values, such as XboxOne, to our internal platform representation
                    platforms: camelizedResponseData.platforms
                        .map(normalizeEventPlatforms)
                        .map(event => {
                            // Convert to uppercase to avoid mismatch with API response
                            event.platform = event.platform.toUpperCase();
                            return event;
                        })  // Filter out unknown platforms
                        .filter(event => PLATFORM_TYPES[event.platform])
                };
            })
            .catch((error: AxiosError) => {
                throw EventManagerError.fromAxiosError(error);
            });
    }

    public applyOwnership(envName: string, data: IOwnershipRequest): Promise<Partial<IEventOwnershipResponse> | EventManagerError> {
        const endpointToCall = buildEventManagerPostURL(envName, data);

        return axios
            .post(
                endpointToCall,
                {},
                {
                    headers: createEventReuqestHeaders({
                        ticket: data.UbisoftConnectTicket,
                        applicationId: data.applicationId
                    })
                }
            )
            .then((response: AxiosResponse<IEventOwnershipResponse>) => {
                return camelize<IEventOwnershipResponse>(response.data);
            })
            .catch((error: AxiosError) => {
                throw EventManagerError.fromAxiosError(error);
            });
    }
}

const eventManager = new EventManagerService();

export default eventManager;
